import { deviceSession } from '@/privacy/deviceStorage';
import { useLayoutEffect } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { barTitleOf } from '@featit/shared/bar-title';
import { chromeLabelText, type ChromeTranslateFn } from '@featit/shared/chrome-label';
import { historyIndex } from './backNavigation';
import {
  BACK_LABEL_MAX,
  backLabelOf,
  phoneTabRootOf,
  routeParentOf,
  type PhoneTabRootType,
} from './routeParents';
import { quotableAddressPart } from '@/routing/quotableAddressPart';
import { englishT, useT } from '@/i18n/LocaleProvider';

/**
 * I-1352 (`docs/concept-iphone-ux.md`, IL-18): the back label names the page Back returns to, read
 * from the router's history entry — never a string a page chose for itself.
 *
 * Each entry remembers the address it was pushed from, by its router key: a push records the page
 * on screen before it, a replace hands the replaced entry's record to the new one (⌃ ⌄ step through
 * a list by replacing, so Back still returns to the list), and a pop records nothing — the entry it
 * lands on kept its own. Kept in `sessionStorage`, so a reload, whose entries keep their keys, keeps
 * the label.
 *
 * Recorded while the router root renders (`HistoryEntries`), before the page below it renders, so the
 * back row's first frame already says where Back goes.
 */

const STORAGE_PREFIX = 'featit:history-from:';
/** F-0567 (NX-6): the entry the address above was pushed from, so a way back can walk past it. */
const KEY_STORAGE_PREFIX = 'featit:history-from-key:';

/** The entries recorded this session, in memory too: a blocked store still names the pages. */
const recorded = new Map<string, string>();
const recordedKeys = new Map<string, string>();

function keyOf(entryKey: string): string {
  return `${STORAGE_PREFIX}${entryKey}`;
}

function store(
  map: Map<string, string>,
  storageKey: string,
  entryKey: string,
  value: string | null,
) {
  if (value === null) map.delete(entryKey);
  else map.set(entryKey, value);
  try {
    if (value === null) deviceSession.removeItem(storageKey);
    else deviceSession.setItem(storageKey, value);
  } catch {
    // Without storage the label lasts until a reload, then says "Back".
  }
}

function write(entryKey: string, from: string | null, fromKey: string | null): void {
  store(recorded, keyOf(entryKey), entryKey, from);
  store(recordedKeys, `${KEY_STORAGE_PREFIX}${entryKey}`, entryKey, fromKey);
}

/** The router key of the entry `entryKey` was pushed from, or `null` when it is not known. */
export function previousEntryKeyOf(entryKey: string): string | null {
  const kept = recordedKeys.get(entryKey);
  if (kept !== undefined) return kept;
  try {
    return deviceSession.getItem(`${KEY_STORAGE_PREFIX}${entryKey}`);
  } catch {
    return null;
  }
}

/** The address the entry `entryKey` was pushed from, or `null` when it is not known. */
export function previousAddressOf(entryKey: string): string | null {
  const kept = recorded.get(entryKey);
  if (kept !== undefined) return kept;
  try {
    return deviceSession.getItem(keyOf(entryKey));
  } catch {
    return null;
  }
}

interface IEntry {
  readonly key: string;
  readonly address: string;
}

/**
 * What a navigation to `next` records, given the entry that was on screen before it (`previous`,
 * `null` for the first one). Exported for its unit test.
 */
export function recordNavigation(
  navigationType: NavigationType | `${NavigationType}`,
  previous: IEntry | null,
  next: IEntry,
): void {
  if (previous === null || previous.key === next.key) return;
  if (navigationType === NavigationType.Push) write(next.key, previous.address, previous.key);
  else if (navigationType === NavigationType.Replace) {
    write(next.key, previousAddressOf(previous.key), previousEntryKeyOf(previous.key));
  }
}

/** A tab root's name, as its tab says it (`SHELL_TAB_DEFINITIONS`), in the reader's language. */
const TAB_TITLES = {
  decisions: 'chromeTitles.decisions',
  activity: 'chromeTitles.activity',
  items: 'chromeTitles.items',
  roadmap: 'chromeTitles.roadmap',
  agents: 'chromeTitles.agents',
} as const satisfies Record<PhoneTabRootType, string>;

/** A custom page's own title by its address, or `null` when it is not known (IL-18b). */
export type PageTitleOfType = (pathname: string) => string | null | undefined;

/**
 * What the back control calls the page at `address`: a record by its key ("F-0530"), a tab's root
 * by the tab's name ("Items"), any other page by its name — and "Back" when that name is longer
 * than the row has room for (S-4: at most 12 characters), all said in the reader's language by `t`
 * and measured as said (T-0838, IN-29).
 *
 * IL-18b (I-2531): a custom page is called by its own title, from `pageTitleOf`; one whose title is
 * not known says "Back", never the fallback "Home" its address alone is named by.
 */
export function backLabelOfAddress(
  address: string,
  t: ChromeTranslateFn,
  pageTitleOf?: PageTitleOfType,
): string {
  const pathname = address.split(/[?#]/, 1)[0];
  const tab = phoneTabRootOf(pathname);
  const pageTitle = pageTitleOf?.(pathname) || null;
  const own = barTitleOf(pathname, { pageTitle });
  if (!tab && own.view === 'page' && !own.namesTheView) return t('chromeTitles.back');
  const label =
    (tab ? t(TAB_TITLES[tab]) : undefined) ||
    routeParentOf(pathname, t, pageTitle)?.recordKey ||
    chromeLabelText(own.label, t);
  // I-1545 (EM-19a): a label from the address is never an unreadable key (`%00`).
  return label && quotableAddressPart(label) && label.length <= BACK_LABEL_MAX
    ? label
    : t('chromeTitles.back');
}

/**
 * The label of a pushed screen's back control, whose way back is `parent` with no history:
 *
 * - with nothing behind it in the app (a cold open) Back replaces the screen with `parent`, so the
 *   label is the parent's — except an address followed by a document load (`arrivedByDocument`),
 *   whose Back steps through the document's history to a page this app cannot name: "Back";
 * - with an entry behind it, Back pops to that entry: the parent's own name when it is the parent's
 *   list, else the entry's (`backLabelOfAddress`), else — an entry recorded by no build of this
 *   app — "Back".
 */
export function historyBackLabelIn(
  t: ChromeTranslateFn,
  parent: { readonly path: string; readonly label: string },
  entryKey: string,
  {
    arrivedByDocument = false,
    pageTitleOf,
  }: { arrivedByDocument?: boolean; pageTitleOf?: PageTitleOfType } = {},
): string {
  const back = t('chromeTitles.back');
  if (historyIndex() === 0) return arrivedByDocument ? back : backLabelOf(parent, t);
  const from = previousAddressOf(entryKey);
  if (from === null) return back;
  if (from.split(/[?#]/, 1)[0] === parent.path) return backLabelOf(parent, t);
  return backLabelOfAddress(from, t, pageTitleOf);
}

/**
 * ⚠️ `historyBackLabelIn` in English, for the one caller whose parent's name is still an English
 * literal: `SettingsRail.tsx`'s "Settings" (T-0838). It goes when that page says its back label
 * through the catalogue; every other caller passes its page's `t`.
 */
export function historyBackLabel(
  parent: { readonly path: string; readonly label: string },
  entryKey: string,
  options?: { arrivedByDocument?: boolean; pageTitleOf?: PageTitleOfType },
): string {
  return historyBackLabelIn(englishT, parent, entryKey, options);
}

/** The router's key for the entry on screen, read from the window's history (`{ usr, key, idx }`). */
export function currentEntryKey(): string {
  const state = window.history.state as { key?: unknown } | null;
  return typeof state?.key === 'string' ? state.key : 'default';
}

/** `historyBackLabel` for the entry on screen. */
export function useHistoryBackLabel(
  parent: { readonly path: string; readonly label: string },
  options?: { arrivedByDocument?: boolean; pageTitleOf?: PageTitleOfType },
): string {
  const { key } = useLocation();
  const t = useT();
  return historyBackLabelIn(t, parent, key, options);
}

let committed: IEntry | null = null;

/**
 * Renders nothing; records each navigation as the router root renders it. Mounted once, above every
 * route (`App.tsx`), in a browser and in the app alike.
 */
export function HistoryEntries(): null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const entry = { key: location.key, address: `${location.pathname}${location.search}` };
  // During render, so the page rendered below this one reads it; writing the same record twice is
  // harmless, so a render React discards or repeats records nothing wrong.
  if (committed?.key !== entry.key) recordNavigation(navigationType, committed, entry);
  useLayoutEffect(() => {
    committed = entry;
    // The entry is identified by its key; its address is read with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.key, entry.address]);
  return null;
}

/** For tests: forget what was recorded and which entry was on screen. */
export function resetHistoryEntries(): void {
  recorded.clear();
  recordedKeys.clear();
  committed = null;
}
